import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { type Permission } from '@/utils/permissions'
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  hasAnyRole,
  hasAllRoles,
  canAccess,
  getAllUserPermissions,
  isAdmin,
} from '@/utils/authorization'

/**
 * Composable for checking user permissions and roles throughout components
 * Provides reactive permission checks based on current authenticated user
 *
 * @returns Object with permission checking methods
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { usePermissions } from '@/composables/usePermissions'
 *
 * const { can, canAny, canAll } = usePermissions()
 *
 * const canCreateRestaurant = can('restaurant:create')
 * const canEditOrDelete = canAny(['restaurant:delete', 'restaurant:create'])
 * </script>
 *
 * <template>
 *   <button v-if="canCreateRestaurant" @click="createRestaurant">
 *     Add Restaurant
 *   </button>
 * </template>
 * ```
 */
export const usePermissions = () => {
  const authStore = useAuthStore()

  const user = computed(() => authStore.currentUser)

  /**
   * Check if user has a specific permission
   */
  const can = (permission: Permission | string): boolean => {
    return hasPermission(user.value, permission)
  }

  /**
   * Check if user has ANY of the provided permissions (OR logic)
   */
  const canAny = (permissions: (Permission | string)[]): boolean => {
    return hasAnyPermission(user.value, permissions)
  }

  /**
   * Check if user has ALL of the provided permissions (AND logic)
   */
  const canAll = (permissions: (Permission | string)[]): boolean => {
    return hasAllPermissions(user.value, permissions)
  }

  /**
   * Check if user has a specific role
   */
  const hasUserRole = (roleName: string): boolean => {
    return hasRole(user.value, roleName)
  }

  /**
   * Check if user has ANY of the provided roles (OR logic)
   */
  const hasUserAnyRole = (roleNames: string[]): boolean => {
    return hasAnyRole(user.value, roleNames)
  }

  /**
   * Check if user has ALL of the provided roles (AND logic)
   */
  const hasUserAllRoles = (roleNames: string[]): boolean => {
    return hasAllRoles(user.value, roleNames)
  }

  /**
   * Check if user can access a resource (complex authorization checks)
   */
  const canAccessResource = (options: {
    permissions?: (Permission | string)[]
    permissionLogic?: 'any' | 'all'
    roles?: string[]
    roleLogic?: 'any' | 'all'
    combineLogic?: 'and' | 'or'
  }): boolean => {
    return canAccess(user.value, options)
  }

  /**
   * Get all permissions for the current user
   */
  const getUserPermissions = (): string[] => {
    return getAllUserPermissions(user.value)
  }

  /**
   * Check if current user is an admin (bypasses all permission checks)
   */
  const isUserAdmin = (): boolean => {
    return isAdmin(user.value)
  }

  return {
    can,
    canAny,
    canAll,
    hasUserRole,
    hasUserAnyRole,
    hasUserAllRoles,
    canAccessResource,
    getUserPermissions,
    isUserAdmin,
    user,
  }
}
